<template>
  <div class="min-h-screen bg-primary w-full h-full mt-[60px] lg:mt-0 themeTransition">
    <div v-if="!isLoading" class="sectionContainer place-content-start pt-[100px]">
      <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
        <!-- content begin -->
        <div
          class="col-start-2 col-span-12 flex flex-col lg:flex-row md:col-start-2 md:col-span-10 lg:col-start-2 lg:col-span-11 lg:mb-[120px]"
        >
          <header1
            :text="properties.title"
            :isUmbraco="isUmbraco"
            :theme="themeType"
            classes="mb-[60px] lg:mb-30"
          />
        </div>
        <div class="col-start-2 col-span-12 lg:col-start-4 lg:col-span-8">
          <search-with-auto-complete
            placeholder="Search"
            :initial-value="initialValue"
            :theme-type="themeType"
            :classes="searchStyles"
            @handle-search="getSearchResults"
            @handle-clear="resetResults"
          />
          <paragraph
            :text="totalCount ? `${totalCount} search results` : 'No search results'"
            :is-umbraco="isUmbraco"
            :theme="themeType"
            classes="mt-4 font-bold text-sm"
          />
        </div>
        <div
          v-if="totalCount"
          class="relative flex overflow-x-auto 2xl:overflow-x-visible no-scrollbar col-start-1 lg:col-start-4 col-span-full"
        >
          <filter-tag
            v-for="category in categories"
            :label="category.title"
            :selected="category.title === activeFilter"
            :count="category.totalCount"
            :theme="themeType"
            classes="mr-3 shrink-0 first:mx-5 md:first:ml-8 md:last:mr-8 lg:last:mr-[108px] lg:first:ml-0"
            @handleClick="setActiveFilter(category.title)"
          />
        </div>
        <div class="col-start-2 col-span-12 lg:col-start-4 lg:col-span-8 pb-20">
          <list-item
            v-for="(result, index) in results"
            :key="index"
            :tag="result.category"
            :title="result.title || ''"
            :description="result.content || ''"
            :is-umbraco="isUmbraco"
            :is-link="true"
            :href="result.url"
            :theme="theme"
          />
        </div>
        <div
          v-if="showLoadMore"
          class="col-start-6 col-span-4 md:col-span-2 md:col-start-7 mb-36 mt-10"
        >
          <custom-button
            label="Load more"
            theme="light"
            type="secondary"
            @handleClick="handleLoadMore"
          />
        </div>
        <!-- content end -->
      </page-wrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { changeTheme, getThemeType } from "@/util/helper";
import Header1 from "@components/Typography/Header1.ce.vue";
import PageWrapper from "@components/PageWrapper.vue";
import { Theme } from "@/types/types";
import { getPage, getPageByID, getSearchResult, getTranslations } from "@/util/api";
import { ISearchResult, ISearchResultsData } from "@/types/components/ISearchResultData";
import Paragraph from "@components/Typography/Paragraph.ce.vue";
import FilterTag from "@components/Atoms/FilterTag/FilterTag.ce.vue";
import ListItem from "@molecules/ListItem/ListItem.ce.vue";
import { getPathForPageData } from "@/util/getPathForPageData";
import CustomButton from "@atoms/Button/CustomButton.ce.vue";
import SearchWithAutoComplete from "@/components/Atoms/SearchField/SearchWithAutoComplete.vue";

const isLoading = ref(false);
const isUmbraco = ref(false);

const pageGUI = ref<string | undefined>();
const pageData = ref<any | null>(null);
const searchResults = ref<ISearchResultsData | null>(null);
const allSearchResults = ref<ISearchResultsData | null>(null);
const allResultsTranslation = ref<string>("");

const activeFilter = ref("");
const startIndex = ref(0);

const theme = ref<Theme>("sand");
const results = ref<ISearchResult[] | null>(null);
const showLoadMore = ref(false);
const searchParam = ref<string>("");
const pageKey = ref<string | undefined>();

const initialValue = computed(() => {
  if (!searchParam.value) return "";
  const inputValue = searchParam.value.replace(/%20/g, " ");
  return inputValue;
});

const fetchInitialData = async () => {
  pageKey.value = document.documentElement.dataset.id;
  // Fetch page and translation data
  const pageParams = getPathForPageData();
  handleSearchWord();
  if (!pageParams?.path) return;

  [pageData.value, allResultsTranslation.value] = await Promise.all([
    getPageByID(pageKey.value, pageParams?.isPreview),
    //getPage(pageParams?.path, pageParams?.isPreview),
    getTranslations("AllResults"),
  ]);
  allSearchResults.value = await getSearchResult(
    searchParam.value,
    pageData.value.route.startItem.path
  );

  activeFilter.value = allResultsTranslation.value;
  if (allSearchResults.value) {
    if (parseInt(allSearchResults.value.totalCount) > 10) {
      showLoadMore.value = true;
    }
  }
  handlePageData();
  const r = await handleSearch();
  if (r) {
    results.value = r.results;
  }
};

const handleSearchWord = () => {
  const search = new URLSearchParams(window.location.search);
  const searchWord = search.get("search");
  if (searchWord) {
    setSearchParam(searchWord);
  }
};

const handlePageData = () => {
  if (!pageData.value) return;
  pageGUI.value = pageData.value.route.startItem.id;
  updateTheme();
  isLoading.value = false;
};

const updateTheme = () => {
  console.log("properties.value.backgroundColor: ", properties.value.backgroundColor);
  const backgroundColor = properties.value.backgroundColor;
  if (backgroundColor?.label) {
    theme.value = backgroundColor.label;
  } else if (backgroundColor) {
    theme.value = backgroundColor;
  }
  if (theme.value) changeTheme(theme.value);
};

const handleSearch = async () => {
  if (!searchParam.value) return;
  const category =
    activeFilter.value === allResultsTranslation.value ? undefined : activeFilter.value;
  searchResults.value = await getSearchResult(
    searchParam.value,
    pageData.value.route.startItem.path,
    startIndex.value,
    category
  );
  return searchResults.value;
};

const setSearchParam = (input: string) => {
  searchParam.value = input;
};

const setActiveFilter = async (category: string) => {
  activeFilter.value = category;
  startIndex.value = 0;
  const r = await handleSearch();
  if (r) {
    results.value = r.results;
  }
  loadMore(categories.value.find((c) => c.title === category)?.totalCount);
};

watch(activeFilter, (value, oldValue) => {
  if (value !== oldValue) {
    setActiveFilter(value);
  }
});

const properties = computed(() => {
  return pageData.value?.properties;
});

const totalCount = computed(() => {
  return searchResults.value?.totalCount;
});

const categories = computed(() => [
  {
    title: allResultsTranslation.value,
    totalCount: allSearchResults.value?.totalCount || "",
  },
  ...(allSearchResults.value?.categories || []),
]);

onBeforeMount(fetchInitialData);

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const searchStyles = computed(() => {
  if (!theme.value) return "bg-sand";
  return `bg-${theme.value} w-full`;
});

const getSearchResults = async (input: string) => {
  searchParam.value = encodeURIComponent(input);
  const newQuery = `?search=${searchParam.value}`;
  history.pushState({}, "", newQuery);

  const r = await handleSearch();
  if (!r) return;
  allSearchResults.value = r;
  results.value = r.results;
  loadMore(r.totalCount);
};

const resetResults = () => {
  allSearchResults.value = null;
  searchResults.value = null;
  results.value = null;
};

function loadMore(totalResults?: string) {
  if (!totalResults) return;
  return (showLoadMore.value = parseInt(totalResults) > startIndex.value + 10);
}

async function handleLoadMore() {
  startIndex.value = startIndex.value === 0 ? 11 : startIndex.value + 10;
  const addedResults = await handleSearch();
  if (!addedResults || !results.value) return;
  results.value = results.value.concat(addedResults.results);
  const totalCount = categories.value.find((c) => c.title === activeFilter.value)
    ?.totalCount;
  loadMore(totalCount);
}
</script>
